import {
    type ReviewResultDto,
    type StudyCardDto,
    type StudyCounts,
    type StudyFilter,
    Rating,
    applyReviewToCounts,
    autoRate
} from "@eb/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Eye, Keyboard, RotateCcw, Star } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnalysisPanel } from "../components/AnalysisPanel";
import { QuestionView } from "../components/QuestionView";
import { Badge, Button, Card, EmptyState, ErrorBanner, ProgressBar, Spinner, cx } from "../components/ui";
import { api } from "../lib/api";
import { RATING_KEYS, RATING_LABELS, formatInterval } from "../lib/format";

type StudyMode = "flashcard" | "quiz";

const FILTERS: { value: StudyFilter; label: string; hint: string }[] = [
    { value: "MIXED", label: "混合", hint: "先复习到期的，再学新题" },
    { value: "DUE_ONLY", label: "只复习", hint: "只做今天到期的旧题" },
    { value: "NEW_ONLY", label: "只学新题", hint: "只看没学过的题" },
    { value: "STARRED", label: "收藏", hint: "只看你标星的题" },
    { value: "DIFFICULT", label: "易错题", hint: "做过的题按错得多少排序，不限于错题" },
    { value: "MISTAKES", label: "错题集", hint: "只做你答错过、还没移除的题" }
];

/** A card whose next due is this close counts as "still in this session". */
const SAME_SESSION_MS = 12 * 60_000;

const RATING_STYLES: Record<Rating, string> = {
    [Rating.Again]: "border-bad text-bad hover:bg-bad-soft",
    [Rating.Hard]: "border-warn text-warn hover:bg-warn-soft",
    [Rating.Good]: "border-good text-good hover:bg-good-soft",
    [Rating.Easy]: "border-brand text-brand hover:bg-brand-soft"
};

export function StudyPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = (searchParams.get("filter") as StudyFilter | null) ?? "MIXED";
    // Optional knowledge-topic restriction, entered from a topic page's "练习这一类".
    const topic = searchParams.get("topic") ?? undefined;
    const queryClient = useQueryClient();

    const [mode, setMode] = useState<StudyMode>(
        () => (localStorage.getItem("eb:studyMode") as StudyMode | null) ?? "quiz"
    );
    useEffect(() => localStorage.setItem("eb:studyMode", mode), [mode]);

    // The working queue is local state seeded from the server: cards answered "Again"
    // are re-appended so the short learning steps actually happen inside this session.
    const [queue, setQueue] = useState<StudyCardDto[]>([]);
    const [index, setIndex] = useState(0);
    const [revealed, setRevealed] = useState(false);
    const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
    const [session, setSession] = useState({ answered: 0, correct: 0 });
    // The header counters are seeded from the batch and then advanced locally per
    // accepted review: refetching the queue would reset the learner's position.
    const [counts, setCounts] = useState<StudyCounts | null>(null);
    // Which cards have been answered in this session, so a card re-shown by a learning
    // step is not subtracted from "due" a second time.
    const answeredIds = useRef(new Set<number>());
    // Set synchronously on submit, before React re-renders with isPending: a held key or
    // a double tap must not send the same card twice and skip ahead in the queue.
    const inFlight = useRef(false);
    // Reviews whose server write failed: shown to the learner instead of silently
    // pretending the answer counted.
    const [failedWrites, setFailedWrites] = useState(0);
    const shownAt = useRef<number>(Date.now());

    const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
    const queueQuery = useQuery({
        queryKey: ["queue", filter, topic ?? null],
        queryFn: () => api.study.queue({ filter, limit: 30, ...(topic ? { topic } : {}) })
    });

    // Reset the local queue whenever the server hands us a fresh batch.
    useEffect(() => {
        if (!queueQuery.data) return;
        setQueue(queueQuery.data.cards);
        setCounts(queueQuery.data.counts);
        answeredIds.current = new Set();
        setIndex(0);
        setRevealed(false);
        setSelectedOptionId(null);
        shownAt.current = Date.now();
    }, [queueQuery.data]);

    const current = queue[index];
    const settings = settingsQuery.data?.settings;

    const reviewMutation = useMutation({
        mutationFn: api.study.review,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["stats"] });
            void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        }
    });

    const starMutation = useMutation({
        mutationFn: ({ number, starred }: { number: number; starred: boolean }) =>
            api.questions.star(number, starred),
        onSuccess: (data) => {
            setQueue((previous) =>
                previous.map((item) =>
                    item.card.questionId === data.card.questionId ? { ...item, card: data.card } : item
                )
            );
        }
    });

    const advance = useCallback((requeue: StudyCardDto | null) => {
        if (requeue) {
            // Put it back at the end of the queue rather than dropping it: this is
            // exactly what the 1-minute and 10-minute learning steps are for.
            setQueue((previous) => [...previous, requeue]);
        }
        setIndex((previous) => previous + 1);
        setRevealed(false);
        setSelectedOptionId(null);
        shownAt.current = Date.now();
    }, []);

    /** Fold one accepted review into the queue and the header counters. */
    const acceptReview = useCallback((answered: StudyCardDto, result: ReviewResultDto, wasCorrect: boolean) => {
        const questionId = answered.card.questionId;
        const firstAnswer = !answeredIds.current.has(questionId);
        answeredIds.current.add(questionId);

        setSession((previous) => ({
            answered: previous.answered + 1,
            correct: previous.correct + (wasCorrect ? 1 : 0)
        }));
        setCounts((previous) =>
            previous ? applyReviewToCounts(previous, answered.card, result.card, new Date(), firstAnswer) : previous
        );

        // Requeue with the state the server just wrote: the badge, the rating previews and
        // the next counter update all have to see the card as it is now, not as it was.
        const dueSoon = new Date(result.nextDue).getTime() - Date.now() < SAME_SESSION_MS;
        advance(dueSoon ? { ...answered, card: result.card, previews: result.previews } : null);
    }, [advance]);

    const submitRating = useCallback(
        (rating: Rating, optionId: number | null) => {
            if (!current || inFlight.current) return;
            inFlight.current = true;

            const durationMs = Math.min(3_600_000, Date.now() - shownAt.current);
            const correctOption = current.question.options.find((option) => option.isCorrect);
            const wasCorrect = optionId === null ? rating !== Rating.Again : optionId === correctOption?.id;

            reviewMutation.mutate(
                {
                    questionId: current.question.id,
                    rating,
                    mode: optionId === null ? "FLASHCARD" : "QUIZ",
                    durationMs,
                    selectedOptionId: optionId
                },
                {
                    // Only answers the server accepted count towards the session tally.
                    onSuccess: (result) => acceptReview(current, result, wasCorrect),
                    // Keep the learner moving even if one write failed, but say so: the
                    // card is not requeued and the counter below the header shows how
                    // many answers did not land.
                    onError: () => {
                        setFailedWrites((previous) => previous + 1);
                        advance(null);
                    },
                    onSettled: () => {
                        inFlight.current = false;
                    }
                }
            );
        },
        [acceptReview, advance, current, reviewMutation]
    );

    const handleSelectOption = useCallback(
        (optionId: number) => {
            if (!current || revealed) return;
            setSelectedOptionId(optionId);
            setRevealed(true);
        },
        [current, revealed]
    );

    // In quiz mode the grade is derived from correctness and speed rather than pressed.
    const confirmQuizAnswer = useCallback(() => {
        if (!current || selectedOptionId === null) return;
        const correctOption = current.question.options.find((option) => option.isCorrect);
        const rating = autoRate(selectedOptionId === correctOption?.id, Date.now() - shownAt.current);
        submitRating(rating, selectedOptionId);
    }, [current, selectedOptionId, submitRating]);

    // Keyboard shortcuts: the whole point of a flashcard app is not touching the mouse.
    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
            if (!current) return;

            if (event.key === " " || event.key === "Enter") {
                event.preventDefault();
                if (mode === "flashcard" && !revealed) setRevealed(true);
                else if (mode === "quiz" && revealed) confirmQuizAnswer();
                return;
            }

            const digit = Number.parseInt(event.key, 10);
            if (!Number.isInteger(digit) || digit < 1 || digit > 4) return;
            event.preventDefault();

            if (mode === "quiz" && !revealed) {
                const option = current.question.options[digit - 1];
                if (option) handleSelectOption(option.id);
            } else if (mode === "flashcard" && revealed) {
                submitRating(digit as Rating, null);
            }
        }

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [confirmQuizAnswer, current, handleSelectOption, mode, revealed, submitRating]);

    // Progress counts distinct questions, not card showings. A card the learning steps
    // bring back inside the session is a repeat: it does not grow the denominator and
    // does not advance the numerator, otherwise every answer on a new card (Again, Hard
    // and Good all come back within 10 minutes) made the round look longer.
    const progress = useMemo(() => {
        const seen = new Set<number>();
        for (const item of queue.slice(0, index)) seen.add(item.card.questionId);
        const total = new Set(queue.map((item) => item.card.questionId)).size;
        const currentId = queue[index]?.card.questionId;
        return {
            done: seen.size,
            total,
            isRepeat: currentId !== undefined && seen.has(currentId)
        };
    }, [index, queue]);

    if (queueQuery.isLoading || settingsQuery.isLoading) return <Spinner label="正在准备今天的卡片" />;
    if (queueQuery.isError) {
        return <ErrorBanner message="加载卡片失败" onRetry={() => void queueQuery.refetch()} />;
    }

    return (
        <div className="space-y-5">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {FILTERS.map((option) => (
                        <button
                            key={option.value}
                            title={option.hint}
                            onClick={() => setSearchParams({ filter: option.value, ...(topic ? { topic } : {}) })}
                            className={cx(
                                "rounded-full border px-3 py-1.5 text-sm transition-colors",
                                filter === option.value
                                    ? "border-brand bg-brand-soft font-medium text-brand"
                                    : "border-line bg-surface text-ink-soft hover:text-ink"
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-1 rounded-full border border-line bg-surface p-1">
                    {(["flashcard", "quiz"] as StudyMode[]).map((value) => (
                        <button
                            key={value}
                            onClick={() => setMode(value)}
                            className={cx(
                                "rounded-full px-3 py-1 text-sm transition-colors",
                                mode === value ? "bg-brand text-white" : "text-ink-soft hover:text-ink"
                            )}
                        >
                            {value === "flashcard" ? "背题" : "练题"}
                        </button>
                    ))}
                </div>
            </header>

            {topic ? (
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-brand-soft px-3 py-2 text-sm text-brand">
                    <span>
                        只练知识点 <Link to={`/topics/${topic}`} className="font-medium underline">{topic}</Link>
                    </span>
                    <button onClick={() => setSearchParams({ filter })} className="ml-auto text-xs hover:underline">
                        取消限制
                    </button>
                </p>
            ) : null}

            {counts ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-soft">
                    {/* The queue length follows the filter; the three counts after it describe
                        the whole deck and only move as cards are answered. */}
                    <span>
                        本轮 <strong className="text-ink">{progress.total}</strong> 张
                    </span>
                    <span aria-hidden className="text-line">
                        |
                    </span>
                    <span className="text-xs">总体</span>
                    <span>
                        待复习 <strong className="text-ink">{counts.due}</strong>
                    </span>
                    <span>
                        未学 <strong className="text-ink">{counts.new}</strong>
                    </span>
                    <span>
                        今日新题余额 <strong className="text-ink">{counts.newRemainingToday}</strong>
                    </span>
                    {session.answered > 0 ? (
                        <span className="ml-auto">
                            本次 <strong className="text-ink">{session.answered}</strong> 题 · 正确率{" "}
                            <strong className="text-ink">
                                {Math.round((session.correct / session.answered) * 100)}%
                            </strong>
                        </span>
                    ) : null}
                </div>
            ) : null}

            {failedWrites > 0 ? (
                <p className="rounded-lg bg-bad-soft px-3 py-2 text-sm text-bad">
                    有 {failedWrites} 题的复习记录没有保存成功，未计入本次统计。请检查网络；这些题不会丢，
                    下一轮队列会重新出现。
                </p>
            ) : null}

            {!current ? (
                <SessionDone
                    answered={session.answered}
                    correct={session.correct}
                    onReload={() => {
                        setSession({ answered: 0, correct: 0 });
                        setFailedWrites(0);
                        void queueQuery.refetch();
                    }}
                />
            ) : (
                <>
                    <ProgressBar value={progress.done} max={Math.max(1, progress.total)} />

                    <Card className="p-5 sm:p-7">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge tone={current.card.state === "NEW" ? "brand" : "neutral"}>
                                    {current.card.state === "NEW"
                                        ? "新题"
                                        : `已复习 ${current.card.reps} 次 · 忘记 ${current.card.lapses} 次`}
                                </Badge>
                                {progress.isRepeat ? (
                                    <Badge tone="warn" title="这张卡在本轮已经做过，按学习步骤再出现一次，不计入进度">
                                        复现
                                    </Badge>
                                ) : null}
                            </div>
                            <button
                                onClick={() =>
                                    starMutation.mutate({
                                        number: current.question.number,
                                        starred: !current.card.isStarred
                                    })
                                }
                                aria-pressed={current.card.isStarred}
                                aria-label={current.card.isStarred ? "取消收藏" : "收藏这道题"}
                                className="rounded-lg p-1.5 text-ink-soft transition-colors hover:bg-surface-sunken"
                            >
                                <Star
                                    className={cx("size-5", current.card.isStarred && "fill-warn text-warn")}
                                />
                            </button>
                        </div>

                        <div className="mt-4">
                            <QuestionView
                                question={current.question}
                                revealed={revealed}
                                selectedOptionId={selectedOptionId}
                                showKeyHints={mode === "quiz" && !revealed}
                                // In quiz mode the translation always appears once the learner has
                                // picked an option: the guess was made on the German, and the
                                // reveal is where the meaning helps. Before that, the setting rules.
                                showTranslation={(settings?.showQuestionTranslation ?? false) || (mode === "quiz" && revealed)}
                                {...(mode === "quiz" && !revealed ? { onSelect: handleSelectOption } : {})}
                            />
                        </div>

                        {!revealed && mode === "flashcard" ? (
                            <div className="mt-6 flex flex-col items-center gap-2">
                                <Button onClick={() => setRevealed(true)} className="w-full sm:w-auto">
                                    <Eye className="size-4" aria-hidden />
                                    显示答案
                                </Button>
                                <p className="flex items-center gap-1.5 text-xs text-ink-soft">
                                    <Keyboard className="size-3.5" aria-hidden />
                                    按空格键显示答案
                                </p>
                            </div>
                        ) : null}

                        {revealed ? (
                            <div className="mt-7 space-y-6 border-t border-line pt-6">
                                <AnalysisPanel
                                    analysis={current.question.analysis}
                                    showEnglish={settings?.showEnglish ?? false}
                                    showChinese={settings?.showChinese ?? true}
                                />

                                {mode === "flashcard" ? (
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium text-ink">这道题你记得多牢？</p>
                                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                            {current.previews.map((preview) => (
                                                <button
                                                    key={preview.rating}
                                                    onClick={() => submitRating(preview.rating, null)}
                                                    disabled={reviewMutation.isPending}
                                                    className={cx(
                                                        "flex flex-col items-center gap-0.5 rounded-xl border bg-surface px-3 py-3 transition-colors disabled:opacity-50",
                                                        RATING_STYLES[preview.rating]
                                                    )}
                                                >
                                                    <span className="text-sm font-medium">
                                                        {RATING_LABELS[preview.rating]}
                                                    </span>
                                                    <span className="text-xs opacity-80">
                                                        {formatInterval(preview.days, preview.minutes)}
                                                    </span>
                                                    <span className="text-[10px] text-ink-soft">
                                                        按 {RATING_KEYS[preview.rating]}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <Button
                                        onClick={confirmQuizAnswer}
                                        loading={reviewMutation.isPending}
                                        className="w-full"
                                    >
                                        <Check className="size-4" aria-hidden />
                                        下一题（空格）
                                    </Button>
                                )}
                            </div>
                        ) : null}
                    </Card>

                    <p className="text-center text-xs text-ink-soft">
                        {progress.isRepeat
                            ? `复现 · 已完成 ${progress.done} / ${progress.total} 题`
                            : `第 ${progress.done + 1} / ${progress.total} 题`}{" "}
                        ·{" "}
                        <Link to={`/questions/${current.question.number}`} className="hover:underline">
                            查看完整题目页
                        </Link>
                    </p>
                </>
            )}
        </div>
    );
}

function SessionDone({
    answered,
    correct,
    onReload
}: {
    answered: number;
    correct: number;
    onReload: () => void;
}) {
    if (answered === 0) {
        return (
            <EmptyState
                title="这个筛选下暂时没有卡片"
                description="换一个筛选条件，或者去题库里手动挑几道题练习。今天该复习的可能已经做完了。"
                action={
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={onReload}>
                            <RotateCcw className="size-4" aria-hidden />
                            重新加载
                        </Button>
                        <Link to="/browse">
                            <Button variant="secondary">去题库</Button>
                        </Link>
                    </div>
                }
            />
        );
    }

    // answered can be 0 now that failed writes are not counted.
    const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    return (
        <Card className="space-y-4 p-8 text-center">
            <p className="text-3xl">🎉</p>
            <h2 className="text-lg font-semibold text-ink">这一轮做完了</h2>
            <p className="text-sm text-ink-soft">
                共 {answered} 题，正确 {correct} 题，正确率 {accuracy}%
            </p>
            <div className="flex justify-center gap-2 pt-2">
                <Button onClick={onReload}>
                    <RotateCcw className="size-4" aria-hidden />
                    再来一轮
                </Button>
                <Link to="/stats">
                    <Button variant="secondary">看统计</Button>
                </Link>
            </div>
        </Card>
    );
}
